// Phase 5 — Site Manager cockpit. Scoped to the manager's sites/projects.
// Single-project managers are sent straight to their project hub; multi-site
// managers get this cockpit: daily site tasks + my to-dos + visits + PR reports.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ClipboardCheck, ClipboardList, AlertTriangle, Upload, CheckSquare, HardHat,
    Users, ArrowUpRight, Clock, CalendarDays, FileText, CircleCheck, MapPin, X, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";
import SiteQRPoster from "@/components/SiteQRPoster";

function fmtDate(s) {
    if (!s) return null;
    try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
    catch { return s; }
}

const BRAND = "var(--brand-accent, #C75A1B)";

function Tile({ icon: Icon, label, hint, onClick, disabled, testid }) {
    return (
        <button data-testid={testid} onClick={onClick} disabled={disabled}
            className={`text-left rounded-[12px] border p-4 transition-colors ${disabled ? "opacity-55 cursor-not-allowed bg-[#FAF8F5]" : "bg-white hover:border-[var(--brand-accent,#C75A1B)] hover:bg-[#FBEFE5]"}`}
            style={{ borderColor: "#E5E0DA" }}>
            <div className="w-9 h-9 rounded-[9px] grid place-items-center mb-2.5" style={{ background: disabled ? "#EFEAE3" : `${"var(--brand-accent,#C75A1B)"}1a` }}>
                <Icon className="w-4.5 h-4.5" style={{ color: disabled ? "#9A968F" : BRAND }} />
            </div>
            <div className="text-[13px] font-semibold text-[#1F1F1F]">{label}</div>
            <div className="text-[10.5px] text-[#9A968F] mt-0.5">{hint}</div>
        </button>
    );
}

function Panel({ title, icon: Icon, count, children, testid }) {
    return (
        <div data-testid={testid} className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                <Icon className="w-4 h-4" style={{ color: BRAND }} />
                <h3 className="text-[13.5px] font-semibold text-[#1F1F1F] flex-1">{title}</h3>
                {count != null && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{count}</span>}
            </div>
            {children}
        </div>
    );
}

export default function SiteManagerCockpit() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [toolboxQr, setToolboxQr] = useState(false);      // deliver a Toolbox Talk — talk picker + register QR
    const [wsrBanner, setWsrBanner] = useState(null);        // Friday nudge: weekly site report(s) still due

    useEffect(() => {
        api.get("/dashboard/site-manager").then((r) => setData(r.data)).catch(() => setData({ error: true }));
    }, []);

    useEffect(() => {
        if (new Date().getDay() !== 5) return; // Friday only — the report's due date
        api.get("/weekly-reports/dashboard").then((r) => {
            const weekStart = r.data?.week_start;
            if (sessionStorage.getItem(`wsr_banner_dismissed_${weekStart}`)) return;
            const due = (r.data?.projects || []).filter((p) => p.status !== "submitted");
            if (due.length) setWsrBanner({ weekStart, weekEnd: r.data.week_end, due });
        }).catch(() => {});
    }, []);

    const dismissBanner = () => {
        if (wsrBanner) sessionStorage.setItem(`wsr_banner_dismissed_${wsrBanner.weekStart}`, "1");
        setWsrBanner(null);
    };

    const openWeeklyReport = async () => {
        const projects = data?.projects || [];
        const ctx = projects.length === 1 ? projects[0] : null;
        try {
            const { data: dash } = await api.get("/weekly-reports/dashboard", ctx ? { params: { project_id: ctx.id } } : {});
            const row = ctx ? (dash.projects || []).find((p) => p.project_id === ctx.id) : null;
            if (ctx && !row) {
                toast.error("Weekly Site Reports aren't switched on for this project yet — ask your Priority Risk consultant.");
                return;
            }
            if (row?.report_id) { navigate(`/app/weekly-reports/${row.report_id}`); return; }
            if (row) {
                const { data: report } = await api.post("/weekly-reports", { project_id: ctx.id, week_start: dash.week_start });
                navigate(`/app/weekly-reports/${report.id}`);
                return;
            }
            navigate("/app/weekly-reports");
        } catch {
            navigate("/app/weekly-reports");
        }
    };

    if (!data) return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading your site…</div>;
    if (data.error) return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Could not load your cockpit — please contact your Priority Risk contact.</div>;

    const projects = data.projects || [];
    const sites = data.sites || [];
    const todos = data.my_todos || [];
    const visits = data.upcoming_visits || [];
    const reports = data.recent_reports || [];
    // Context for launched records: single-project managers auto-link; else pick in-flow.
    const ctx = projects.length === 1 ? projects[0] : null;
    const q = (base) => ctx ? `${base}${base.includes("?") ? "&" : "?"}project=${ctx.id}&client=${ctx.client_id}&project_label=${encodeURIComponent(ctx.name)}` : base;

    return (
        <div data-testid="site-manager-cockpit" className="space-y-6">
            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: `2px solid ${BRAND}`, color: "#6F6A64" }}>
                    Site Manager
                </span>
                <h2 className="text-2xl font-bold tracking-tight text-[#1F1F1F] mt-3">Good day, {user?.name?.split(" ")[0] || "there"}</h2>
                <p className="text-[13px] mt-1 flex items-center gap-1.5" style={{ color: "#6F6A64" }}>
                    <MapPin className="w-3.5 h-3.5" /> {sites.map((s) => s.name).join(" · ") || `${projects.length} projects`}
                </p>
                {ctx && (
                    <button data-testid="cockpit-open-project" onClick={() => navigate(`/app/projects/${ctx.id}`)}
                        className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full px-3 py-1.5 border transition-colors hover:bg-[#FBEFE5]"
                        style={{ borderColor: "#E5E0DA", color: BRAND }}>
                        Open {ctx.name} <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {/* Daily site tasks */}
            <Panel title="Daily site tasks" icon={ClipboardList} testid="daily-site-tasks-panel">
                <div className="grid gap-3 p-4" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))" }}>
                    <Tile testid="tile-wsr" icon={ClipboardCheck} label="Weekly WSR report" hint="Start or continue this week's report"
                        onClick={openWeeklyReport} />
                    <Tile testid="tile-qa" icon={ClipboardList} label="QA Checklist" hint="Quality assurance inspection"
                        onClick={() => navigate(q("/app/inspections?new=1&template=QA-INSP"))} />
                    <Tile testid="tile-incident" icon={AlertTriangle} label="Incident / near miss" hint="Report what happened"
                        onClick={() => navigate(q("/app/incidents?new=1"))} />
                    <Tile testid="tile-upload" icon={Upload} label="Upload evidence" hint="Add a photo or document"
                        onClick={() => navigate(q("/app/documents?new=1"))} />
                    <Tile testid="tile-induction" icon={Users} label="Site induction" hint="Run an induction register"
                        onClick={() => navigate(q("/app/inspections?new=1&template=SITE-INDUCTION"))} />
                    <Tile testid="tile-build-induction" icon={ShieldCheck} label="Build my site induction" hint="Clone a master, add assembly points & more"
                        onClick={() => navigate("/app/inductions/build")} />
                    <Tile testid="tile-toolbox" icon={HardHat} label="Toolbox talk" hint={ctx ? "Print a QR register for the crew" : "Open a single project to issue"} disabled={!ctx}
                        onClick={() => ctx && setToolboxQr(true)} />
                </div>
            </Panel>

            {/* Deliver a Toolbox Talk — talk picker + register QR (Phase 4) */}
            {toolboxQr && ctx && (
                <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" data-testid="toolbox-issue-modal" onClick={() => setToolboxQr(false)}>
                    <div className="max-w-xl mx-auto mt-8 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <span className="text-white font-semibold text-[14px]">Deliver a Toolbox Talk</span>
                            <button data-testid="toolbox-issue-close" onClick={() => setToolboxQr(false)} className="text-white/90 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <SiteQRPoster clientId={ctx.client_id} siteId={sites[0]?.id} projectId={ctx.id}
                            siteName={sites[0]?.name} projectName={ctx.name}
                            mode="register" templateCategory="toolbox" pickTemplate templateLabel="Toolbox Talk" />
                        <button data-testid="toolbox-issue-done" onClick={() => setToolboxQr(false)}
                            className="w-full h-11 rounded-lg bg-white/95 text-[#1F1F1F] font-semibold text-[13px] inline-flex items-center justify-center gap-2">
                            Done — back to cockpit
                        </button>
                    </div>
                </div>
            )}

            {/* Site QR / self-service sign-on */}
            {ctx && (
                <SiteQRPoster
                    clientId={ctx.client_id}
                    siteId={sites[0]?.id}
                    projectId={ctx.id}
                    siteName={sites[0]?.name}
                    projectName={ctx.name}
                />
            )}

            {/* Friday nudge — weekly site report(s) still due this week */}
            {wsrBanner && (
                <div data-testid="wsr-friday-banner" className="rounded-[13px] border p-4 sm:p-5 flex items-center gap-4 flex-wrap"
                     style={{ background: "#FBEFE5", borderColor: "#EBCBA8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center flex-shrink-0" style={{ background: "#F2DAAD" }}>
                        <ClipboardCheck className="w-5 h-5" style={{ color: "#9F4212" }} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <div className="text-[13.5px] font-bold text-[#1F1F1F]">It's Friday — your Weekly Site Report is due</div>
                        <div className="text-[11.5px] mt-0.5" style={{ color: "#6F6A64" }}>
                            {wsrBanner.due.length === 1 ? wsrBanner.due[0].project_name : `${wsrBanner.due.length} of your projects still need this week's report`}
                        </div>
                    </div>
                    <button data-testid="wsr-friday-banner-create" onClick={() => navigate("/app/weekly-reports")}
                        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-semibold text-white transition-colors"
                        style={{ background: BRAND }}>
                        Create weekly site report <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid="wsr-friday-banner-dismiss" onClick={dismissBanner} className="text-[#9F4212] hover:text-[#7A3410]">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-5">
                {/* My open to-dos */}
                <Panel title="My open to-dos" icon={CheckSquare} count={todos.length} testid="my-todos-panel">
                    <ul>
                        {todos.map((t) => (
                            <li key={t.id} data-testid={`todo-${t.id}`} onClick={() => navigate(`/app/actions?open=${t.id}`)}
                                className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{t.title}</div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{t.project_name}</div>
                                </div>
                                {t.overdue ? <Pill tone="red"><Clock className="w-3 h-3 inline mr-0.5" />Overdue</Pill>
                                    : t.due_date ? <Pill tone="neutral">{fmtDate(t.due_date)}</Pill> : null}
                                <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND }} />
                            </li>
                        ))}
                        {todos.length === 0 && (
                            <li className="px-5 py-8 text-center">
                                <CircleCheck className="w-7 h-7 mx-auto mb-2" style={{ color: "#5FA35A" }} />
                                <div className="text-[12.5px] font-semibold text-[#1F1F1F]">Nothing outstanding</div>
                            </li>
                        )}
                    </ul>
                </Panel>

                {/* Upcoming PR visits */}
                <Panel title="Upcoming Priority Risk visits" icon={CalendarDays} count={visits.length} testid="cockpit-visits-panel">
                    <ul>
                        {visits.slice(0, 6).map((v) => (
                            <li key={v.id} className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{v.title}</div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{v.project_name}{v.assignee_name ? ` · ${v.assignee_name}` : ""}</div>
                                </div>
                                <Pill tone="blue">{fmtDate(v.scheduled_date)}</Pill>
                            </li>
                        ))}
                        {visits.length === 0 && <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">No upcoming visits scheduled.</li>}
                    </ul>
                </Panel>
            </div>

            {/* Recent PR reports — status only */}
            <Panel title="Recent reports from Priority Risk" icon={FileText} count={reports.length} testid="cockpit-reports-panel">
                <ul>
                    {reports.map((r) => (
                        <li key={r.id} data-testid={`cockpit-report-${r.id}`} onClick={() => navigate(`/app/documents?open=${r.id}`)}
                            className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                            <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "#6F6A64" }} />
                            <div className="min-w-0 flex-1">
                                <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{r.title}</div>
                                <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{r.project_name}</div>
                            </div>
                            <Pill tone="neutral">{r.status}</Pill>
                        </li>
                    ))}
                    {reports.length === 0 && <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">No reports issued yet.</li>}
                </ul>
            </Panel>
        </div>
    );
}
